import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuHash } from 'react-icons/lu'
import { Text } from '@chakra-ui/react'
import { AccessorySelectionType } from '@leid/api-orders'

import { OrderItemInfo } from 'appTypes/orders'
import { ComplementaryItemInfo } from 'components/partials/shared/ProductInfoAndRentalSection/components/AccessoryItems/models'
import apiClient from 'helpers/clients/apiClient'
import { getAvailabilitiesChanges } from 'helpers/utils/availabilities'
import { utcNow } from 'utils/dates'

import { ItemCardStylingProps } from '../../interfaces'
import { ItemCardAmountButton, ItemCardSecondarySpan } from '../../styles'
import { ItemCardListItem } from '../ItemCardListItem'

type Props = {
  amount: number
  onDecreaseAmount: (() => Promise<unknown>) | undefined
  onIncreaseAmount: (() => Promise<unknown>) | undefined
  setAccessorySelectionType: (selectionType: AccessorySelectionType | undefined) => void
  isPaymentStarted: boolean
  isConsumable: boolean
  orderItemInfo: OrderItemInfo
} & ItemCardStylingProps

export const ItemCardAccessoryItemAmount = ({
  amount,
  onDecreaseAmount,
  onIncreaseAmount,
  isBundle,
  isPaymentStarted,
  orderItemInfo,
  isConsumable,
  setAccessorySelectionType,
}: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'statuses'])
  const stylingProps: ItemCardStylingProps = { isBundle }

  const [disableAmountButtons, setDisableAmountButtons] = useState(false)
  const [stockInfo, setStockInfo] = useState<ComplementaryItemInfo | undefined>(undefined)

  useEffect(() => {
    getProductStockAmount(amount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDecreaseAmount = async () => {
    await changeProductAmount(onDecreaseAmount, amount - 1)
  }

  const handleIncreaseAmount = async () => {
    await changeProductAmount(onIncreaseAmount, amount + 1)
  }

  const getProductStockAmount = async (newAmount: number): Promise<void> => {
    setDisableAmountButtons(true)
    const now = utcNow()
    const response = await apiClient.getProductCalendarList(orderItemInfo.orderItem.productId ?? 0, {
      startDate: orderItemInfo.orderItem.rentedFrom ?? now,
    })

    if (!response.success || !response.data) {
      setDisableAmountButtons(false)
      return
    }

    const availabilityData = response.data.locations.find(
      (s) => s.locationId === (orderItemInfo.orderItem.locationId ?? 0),
    )
    if (!availabilityData) {
      setDisableAmountButtons(false)
      setStockInfo({
        amount: 0,
        selectionType: undefined,
      })
      return
    }

    const changes = getAvailabilitiesChanges(
      orderItemInfo.orderItem.rentedFrom ?? now,
      orderItemInfo.orderItem.rentedFrom ?? now,
      availabilityData,
      true,
    )

    let availableAmount = Math.min(...changes)
    if (orderItemInfo.orderItem.customerId === undefined) {
      availableAmount = availableAmount - newAmount
    }

    setStockInfo({ amount: availableAmount, selectionType: availabilityData.accessorySelectionType })
    setAccessorySelectionType(availabilityData.accessorySelectionType)
    setDisableAmountButtons(false)
  }

  const changeProductAmount = async (changeFunc: (() => Promise<unknown>) | undefined, newAmount: number) => {
    if (changeFunc) {
      setDisableAmountButtons(true)

      await changeFunc()
      await getProductStockAmount(newAmount)

      setDisableAmountButtons(false)
    }
  }

  return (
    <>
      {isConsumable && amount > 0 && (
        <>
          <ItemCardListItem luIcon={<LuHash size={12} />} {...stylingProps}>
            <ItemCardSecondarySpan {...stylingProps}>{t('common:Quantity')}: </ItemCardSecondarySpan>
            {onDecreaseAmount && (
              <ItemCardAmountButton
                isDisabled={amount <= 1 || disableAmountButtons || isPaymentStarted}
                onClick={handleDecreaseAmount}
                type="button"
                aria-label="Remove related item"
              >
                <img src="/icons/minus.svg" alt="" />
              </ItemCardAmountButton>
            )}
            {amount}
            {onIncreaseAmount && (
              <ItemCardAmountButton
                isDisabled={!stockInfo || stockInfo.amount <= 0 || disableAmountButtons || isPaymentStarted}
                onClick={handleIncreaseAmount}
                type="button"
                aria-label="Add related item"
              >
                <img src="/icons/plus.svg" alt="" />
              </ItemCardAmountButton>
            )}
            {stockInfo?.selectionType === AccessorySelectionType.MANDATORY && (
              <Text
                color="green.a200"
                fontSize="xs"
                fontWeight={600}
                lineHeight="12px"
                marginBottom="3px"
                marginTop="5px"
              >
                {t('mandatoryAccessoryText')}
              </Text>
            )}
          </ItemCardListItem>
        </>
      )}
    </>
  )
}
